import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated } from 'react-native';
import SvgIcon from './SvgIcon';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useNavigationVisibility } from '../context/NavigationVisibilityContext';
import colors from '../theme/colors';

export default function BottomNav() {
  const navigation = useNavigation();
  const { user, isAuthenticated } = useAuth();
  const { bottomNavAnimation } = useNavigationVisibility();
  const currentRoute = useNavigationState(state => 
    state?.routes?.[state.index]?.name || 'Home'
  );
  
  // Get current route params (to retain memberId)
  const routeParams = useNavigationState(state => 
    state?.routes?.[state.index]?.params || {}
  );
  const selectedMemberId = routeParams?.memberId;

  // Different nav items based on user type
  const getNavItems = () => {
    if (isAuthenticated && user?.userType === 'Mediator') {
      // Show Member Profile and Shared Profile buttons when on these screens
      if (currentRoute === 'MemberProfile' || currentRoute === 'SharedProfiles') {
        return [
          { name: 'Profiles', icon: 'people', screen: 'Profiles' },
          { name: 'Member Profile', icon: 'person-circle', screen: 'MemberProfile', params: { memberId: selectedMemberId } },
          { name: 'Shared Profile', icon: 'share-social', screen: 'SharedProfiles', params: { memberId: selectedMemberId } },
          { name: 'Matches', icon: 'heart', screen: 'SuccessStories' },
        ];
      }
      return [
        { name: 'Profiles', icon: 'people', screen: 'Profiles' },
        { name: 'Features', icon: 'sparkles', screen: 'Features' },
        { name: 'How it works', icon: 'git-network', screen: 'HowItWorks' },
        { name: 'Matches', icon: 'heart', screen: 'SuccessStories' },
      ];
    } else if (isAuthenticated && user?.userType === 'Member') {
      return [
        { name: 'My Profile', icon: 'person', screen: 'MyProfile' },
        { name: 'Shared Profiles', icon: 'people', screen: 'SharedProfiles' },
        { name: 'Features', icon: 'sparkles', screen: 'Features' },
        { name: 'Matches', icon: 'heart', screen: 'SuccessStories' },
      ];
    } else {
      return [
        { name: 'Home', icon: 'home', screen: 'Home' },
        { name: 'Features', icon: 'sparkles', screen: 'Features' },
        { name: 'How it works', icon: 'git-network', screen: 'HowItWorks' },
        { name: 'Matches', icon: 'heart', screen: 'SuccessStories' },
      ];
    }
  };

  const navItems = getNavItems();

  return (
    <Animated.View style={[styles.bottomNav, { transform: [{ translateY: bottomNavAnimation }] }]}>
      {navItems.map((item) => {
        const isActive = currentRoute === item.screen;
        return (
          <TouchableOpacity 
            key={item.screen}
            style={styles.navItem}
            onPress={() => {
              if (item.params) {
                navigation.navigate(item.screen, item.params);
              } else {
                navigation.navigate(item.screen);
              }
            }}
          >
            <SvgIcon 
              name={item.icon} 
              size={24} 
              color={isActive ? colors.secondary : colors.primary} 
            />
            <Text style={[styles.navLabel, { color: isActive ? colors.secondary : colors.primary }]}>
              {item.name}
            </Text>
          </TouchableOpacity>
        );
      })}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  bottomNav: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: colors.white,
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderTopWidth: 1,
    borderTopColor: colors.primary,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 4,
    zIndex: 100,
  },
  navItem: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  navIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  navLabel: {
    fontSize: 12,
    color: colors.primary,
    fontWeight: '500',
  },
});
